/**
 * dd-video-viewport.js
 * Diesel Dispensary — viewport-aware video autoplay
 *
 * Root cause: videos are 1080p @ 7.2Mbps (~15-20MB each).
 * With preload="metadata" the browser pre-fetches the first several MB
 * of every video on page load, hammering bandwidth before JS can intervene.
 *
 * This script:
 * 1. Sets preload="none" on all offscreen videos immediately (before metadata fetch)
 * 2. Uses IntersectionObserver to play/pause based on viewport
 * 3. Restores preload="auto" when entering viewport so video actually loads
 *
 * Real fix: re-encode source videos at 720p / 1-2Mbps (80% smaller).
 */

(function () {
  'use strict';

  // Skip product pages — single video, no problem
  if (
    document.body.classList.contains('template-product') &&
    !document.body.classList.contains('template-collection')
  ) return;

  var THRESHOLD = 0.1;

  // Step 1: Kill preload on ALL videos immediately — before browser fetches metadata
  // This runs synchronously, before DOMContentLoaded
  function killPreload() {
    document.querySelectorAll('video[preload="metadata"], video[preload="auto"]').forEach(function(v) {
      if (!v.closest('.dd-vm__panel')) { // don't touch cart drawer videos
        v.setAttribute('preload', 'none');
        v.setAttribute('data-dd-managed', '');
      }
    });
  }

  // Step 2: IntersectionObserver manages play/pause + preload per viewport
  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      var vm = entry.target;
      var video = vm.querySelector('video[data-dd-managed]');
      if (!video) return;

      if (entry.isIntersecting) {
        // Coming into view — allow loading and play
        video.setAttribute('preload', 'auto');
        vm.setAttribute('autoplay', '');
        video.play().catch(function() {});
      } else {
        // Leaving view — pause and stop loading
        video.pause();
        video.setAttribute('preload', 'none');
        vm.removeAttribute('autoplay');
      }
    });
  }, {
    threshold: THRESHOLD,
    rootMargin: '0px 0px 150px 0px' // load slightly before visible
  });

  function manageVideos() {
    killPreload();
    document.querySelectorAll('video-media[autoplay]').forEach(function(vm) {
      if (vm.hasAttribute('data-dd-observed')) return;
      vm.setAttribute('data-dd-observed', '');
      vm.removeAttribute('autoplay'); // prevent immediate play
      observer.observe(vm);
    });
  }

  // Run as early as possible
  if (document.readyState === 'loading') {
    // Inject a synchronous inline script effect via document.write isn't safe,
    // so we run on DOMContentLoaded which fires before images/video load
    document.addEventListener('DOMContentLoaded', manageVideos);
  } else {
    manageVideos();
  }

  // Handle dynamically injected videos (pagination, filters, quick-view)
  new MutationObserver(function(mutations) {
    var found = false;
    mutations.forEach(function(m) {
      m.addedNodes.forEach(function(n) {
        if (n.nodeType === 1 && (n.matches('video-media') || n.querySelector('video-media'))) {
          found = true;
        }
      });
    });
    if (found) manageVideos();
  }).observe(document.documentElement, { childList: true, subtree: true });

})();
